import type { ChangeEvent, DragEvent, ReactNode, Ref } from "react";
import { useTranslation } from "react-i18next";
import { FileText } from "lucide-react";
import type { Session } from "@/types";
import { formatFileSize } from "@/utils/fileUtils";

export function SessionUploadInfoCard({ session }: { session: Session }) {
  const { t } = useTranslation(["pages", "common"]);
  return (
    <div className="bg-secondary-50 rounded-lg p-4">
      <h3 className="font-medium text-secondary-900 mb-2">
        {t("pages:sessions.upload.common.sessionDetails")}
      </h3>
      <div className="text-sm text-secondary-600 space-y-1">
        <p>
          <strong>{t("pages:sessions.upload.common.track")}</strong>{" "}
          {session.event.track.name}
        </p>
        <p>
          <strong>{t("pages:sessions.upload.common.vehicle")}</strong>{" "}
          {session.vehicle.name ||
            `${session.vehicle.year} ${session.vehicle.maker} ${session.vehicle.model}`}
        </p>
        <p>
          <strong>{t("pages:sessions.upload.common.date")}</strong>{" "}
          {new Date(session.createdAt).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
}

interface FileUploadDropZoneProps {
  isIdle: boolean;
  isUploading: boolean;
  selectedFile: File | null;
  uploadProgress: number;
  statusIcon: ReactNode;
  statusMessage: ReactNode;
  hint: ReactNode;
  children?: ReactNode;
  acceptedFileTypes: string[];
  fileInputRef: Ref<HTMLInputElement>;
  onFileSelect: (e: ChangeEvent<HTMLInputElement>) => void;
}

export function FileUploadDropZone({
  isIdle,
  isUploading,
  selectedFile,
  uploadProgress,
  statusIcon,
  statusMessage,
  hint,
  children,
  acceptedFileTypes,
  fileInputRef,
  onFileSelect,
}: FileUploadDropZoneProps) {
  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      // Create a synthetic event to reuse existing validation logic
      const syntheticEvent = {
        target: { files: [file] },
      } as unknown as ChangeEvent<HTMLInputElement>;
      onFileSelect(syntheticEvent);
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
  };

  return (
    <div
      className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
        selectedFile
          ? "border-green-300 bg-green-50"
          : "border-secondary-300 hover:border-secondary-400"
      }`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      <div className="flex flex-col items-center space-y-4">
        {statusIcon}

        <div>
          <p className="text-sm font-medium text-secondary-900">
            {statusMessage}
          </p>

          {!selectedFile && isIdle && (
            <p className="text-xs text-secondary-500 mt-1">{hint}</p>
          )}

          {children}
        </div>

        {isUploading && (
          <div className="w-full max-w-xs">
            <div className="bg-secondary-200 rounded-full h-2">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        {selectedFile && isIdle && (
          <div className="text-sm text-secondary-600 space-y-1">
            <div className="flex items-center space-x-2">
              <FileText className="h-4 w-4" />
              <span>{selectedFile.name}</span>
            </div>
            <p className="text-xs">{formatFileSize(selectedFile.size)}</p>
          </div>
        )}

        {isIdle && (
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedFileTypes.join(",")}
            onChange={onFileSelect}
            className="hidden"
          />
        )}
      </div>
    </div>
  );
}

export function UploadErrorAlert({ error }: { error: string }) {
  if (!error) return null;
  return (
    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
      {error}
    </div>
  );
}

interface UploadModalActionsProps {
  busy: boolean;
  succeeded: boolean;
  showIdleActions: boolean;
  canSubmit: boolean;
  submitLabel: string;
  onBrowse: () => void;
  onSubmit: () => void;
  onClose: () => void;
}

export function UploadModalActions({
  busy,
  succeeded,
  showIdleActions,
  canSubmit,
  submitLabel,
  onBrowse,
  onSubmit,
  onClose,
}: UploadModalActionsProps) {
  const { t } = useTranslation(["pages", "common"]);
  return (
    <div className="flex justify-end space-x-3 pt-6 border-t border-secondary-200">
      <button onClick={onClose} className="btn-secondary px-4 py-2" disabled={busy}>
        {succeeded
          ? t("pages:sessions.upload.common.close")
          : t("pages:sessions.upload.common.cancel")}
      </button>

      {showIdleActions && (
        <>
          <button onClick={onBrowse} className="btn-secondary px-4 py-2">
            {t("pages:sessions.upload.common.browseFiles")}
          </button>

          <button
            onClick={onSubmit}
            className="btn-primary px-4 py-2"
            disabled={!canSubmit}
          >
            {submitLabel}
          </button>
        </>
      )}
    </div>
  );
}
